import {hpcAttrs} from '@github-ui/hpc'
import {useLocation, useNavigation, useSearchParams} from '@github-ui/react-router'
import type {Location} from '@github-ui/react-router'
import {Banner, Button, Pagination} from '@primer/react'
import {announce} from '@github-ui/aria-live'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {PullsList} from '@github-ui/shared-dashboard-lists/pulls/PullsList'
import {SharedDashboardBlankslate} from '@github-ui/shared-dashboard-lists/shared/SharedDashboardBlankslate'

import {PullsDashboardInboxSkeleton} from '../components/PullsDashboardInboxSkeleton'
import {useEnrichedPulls} from '../hooks/use-enriched-pulls'
import {usePullsDashboardState} from '../hooks/use-pulls-dashboard-state'
import {addSortToQuery, getSortFromQuery} from '@github-ui/dashboard-sorting-dropdown/query'
import {usePullsDashboardFetchedQuery, usePullsDashboardPayload} from '../contexts/PullsDashboardSurfaceStateContext'
import {useUpdateCompactModeSetting} from '@github-ui/shared-dashboard-surface/hooks/use-update-compact-mode-setting'
import {
  useMarkScreenReaderAnnouncementPending,
  useScreenReaderAnnouncements,
  useSuppressScreenReaderAnnouncement,
} from '@github-ui/shared-dashboard-surface/hooks/use-screen-reader-announcements'
import {DEFAULT_SORTING_KEY} from '@github-ui/shared-dashboard-surface/constants/pulls-sorting'
import {PULLS_INBOX_PATH, PULLS_SEARCH_PATH} from '../constants/routes'
import {getListResultsLocation} from '../hooks/use-list-results'
import {queryMap} from '../helpers/query-resolver'
import {
  addStateToQueryWithLegacyAliases,
  analyzeStateQuery,
} from '@github-ui/shared-dashboard-surface/helpers/open-closed-toggle'
import {
  addRepoFilterToQuery,
  addAuthorFilterToQuery,
  addLabelFilterToQuery,
  addAssigneeFilterToQuery,
} from '@github-ui/shared-dashboard-surface/helpers/query-filters'
import {useAnalytics} from '@github-ui/use-analytics'
import {useEffect, useRef, useState} from 'react'

const REFRESH_ERROR_ANNOUNCEMENT = 'Unable to refresh pull requests. Showing previously loaded results.'

function isSameLocation(a: Location, b: Location): boolean {
  return a.pathname === b.pathname && a.search === b.search
}

export function shouldRenderInboxSkeleton(
  navigationLocation: Location | undefined,
  currentLocation: Location,
): boolean {
  return Boolean(
    navigationLocation &&
    !isSameLocation(navigationLocation, currentLocation) &&
    navigationLocation.pathname === PULLS_INBOX_PATH,
  )
}

export function PullsDashboardSurfaceContentComponent() {
  const navigation = useNavigation()
  const location = useLocation()

  // From: https://github.com/github/github-ui/blob/48fbe572f28ce54e40331641e1edc91160edb4a3/packages/repos-finder/routes/ReposFinderPageFuture.tsx#L29-L38
  // We compare navigation.location (the pending destination) with the current location
  // to determine if we're navigating to a *different* page. Using navigation.state === 'loading'
  // alone isn't sufficient because it also returns true when re-clicking the same sidebar item
  // triggers a background refetch, which we don't want to show as a full page loading state.
  //
  // We also can't rely on `isLoading` from `useSuspenseRouteQuery` because the route query is
  // blocking - the component suspends until data is fetched, so by the time we have access to
  // the query result, the data is already loaded. This doesn't give us a chance to show the
  // skeleton during navigation. Instead, we detect navigation at this level before suspense kicks in.
  const isNavigating = Boolean(navigation.location && !isSameLocation(navigation.location, location))

  const [searchParams] = useSearchParams()
  const searchQuery = searchParams.has('q')
  const isOnSearchPageWithNoQuery = location.pathname === PULLS_SEARCH_PATH && !searchQuery

  if (shouldRenderInboxSkeleton(navigation.location, location)) {
    return <PullsDashboardInboxSkeleton />
  }

  return (
    <ErrorBoundary
      boundaryName="PullsDashboardContent"
      fallback={
        <div className="tmp-my-3">
          <SharedDashboardBlankslate dashboardType="pull-requests" variant="error" />
        </div>
      }
    >
      {isOnSearchPageWithNoQuery && !isNavigating ? (
        <SharedDashboardBlankslate dashboardType="pull-requests" variant="emptySearch" />
      ) : (
        <PullsDashboardPageContent isNavigating={isNavigating} />
      )}
    </ErrorBoundary>
  )
}

function PullsDashboardPageContent({isNavigating}: {isNavigating: boolean}) {
  const {
    pulls,
    pageInfo,
    isFetching: isResultsFetching,
    isPending,
    failedRefetch,
    retryFailedRefetch,
    error,
  } = useEnrichedPulls()
  const compactMode = usePullsDashboardPayload().compactMode ?? false
  const {
    currentPage: requestedPage,
    methods,
    isLoading,
    results: displayedData,
  } = usePullsDashboardState({
    results: pulls,
    pageInfo,
    error,
  })
  const fetchedQuery = usePullsDashboardFetchedQuery()
  const location = useLocation()
  const {sendAnalyticsEvent} = useAnalytics()
  const isFetching = isNavigating || isLoading || (fetchedQuery.length > 0 && (!!isResultsFetching || !!isPending))
  const stateQueryAnalysis = analyzeStateQuery(fetchedQuery, {legacyAliases: true})
  const suppressScreenReaderAnnouncement = useSuppressScreenReaderAnnouncement()
  const markScreenReaderAnnouncementPending = useMarkScreenReaderAnnouncementPending()
  const failedRefetchQuery = failedRefetch?.query
  const failedRefetchPage = failedRefetch?.page
  const failedRefetchUpdateCount = failedRefetch?.updateCount
  const isCurrentFailedRefetch = failedRefetchQuery === fetchedQuery && failedRefetchPage === requestedPage
  const hasDisplayedError = Boolean(displayedData.error)
  const resultsRegionRef = useRef<HTMLDivElement>(null)
  const [retryingTarget, setRetryingTarget] = useState<{query: string; page: number}>()
  const isRetrying = retryingTarget?.query === fetchedQuery && retryingTarget.page === requestedPage
  const recoveredRetryRef = useRef<{query: string; page: number; focusOrigin: HTMLElement | null} | undefined>(
    undefined,
  )
  const retryAttemptRef = useRef(0)

  useEffect(() => {
    if (!isCurrentFailedRefetch || failedRefetchQuery === undefined || failedRefetchPage === undefined) return
    suppressScreenReaderAnnouncement(fetchedQuery, displayedData.pageInfo.currentPage)
    announce(REFRESH_ERROR_ANNOUNCEMENT)
  }, [
    displayedData.pageInfo.currentPage,
    failedRefetchPage,
    failedRefetchQuery,
    failedRefetchUpdateCount,
    fetchedQuery,
    isCurrentFailedRefetch,
    suppressScreenReaderAnnouncement,
  ])

  useEffect(() => {
    const recoveredRetry = recoveredRetryRef.current
    if (!recoveredRetry || isRetrying || isCurrentFailedRefetch) return

    recoveredRetryRef.current = undefined
    if (recoveredRetry.query !== fetchedQuery || recoveredRetry.page !== requestedPage) return

    markScreenReaderAnnouncementPending(recoveredRetry.query, recoveredRetry.page)
    const focusStayedWithRetry =
      recoveredRetry.focusOrigin !== null &&
      (document.activeElement === recoveredRetry.focusOrigin ||
        (!recoveredRetry.focusOrigin.isConnected && document.activeElement === document.body))
    if (focusStayedWithRetry) resultsRegionRef.current?.focus()
  }, [
    fetchedQuery,
    isCurrentFailedRefetch,
    isRetrying,
    markScreenReaderAnnouncementPending,
    requestedPage,
    retryingTarget,
  ])

  const {emptyListState} = useScreenReaderAnnouncements(
    'pull-requests',
    isFetching || hasDisplayedError,
    displayedData.pageInfo.currentPage,
    fetchedQuery,
    DEFAULT_SORTING_KEY,
    displayedData.pageInfo.totalCount,
    displayedData.pageInfo.totalPages,
  )

  const {updateCompactModeSetting} = useUpdateCompactModeSetting(compactMode, 'dashboard_surface_compact_mode', {
    persistAsPullsPreference: true,
  })

  const getStateFilterLinkProps = (state: 'open' | 'closed') => {
    const query = addStateToQueryWithLegacyAliases(fetchedQuery, state)
    return {
      to: getListResultsLocation({
        q: query,
        page: 1,
        defaultFilterValue: location.pathname === PULLS_INBOX_PATH ? '' : (queryMap().authored ?? ''),
        pathname: location.pathname,
      }),
      state: (location as Location & {state?: unknown}).state,
      reloadDocument: false,
      onClick: () => {
        sendAnalyticsEvent('pull-requests.search', 'UPDATE_AND_SUBMIT_QUERY', {sort: getSortFromQuery(query)})
        sendAnalyticsEvent('pull-requests.search', 'LIST_RESULTS', {query})
      },
    }
  }

  const handleFilterSelect = (type: 'repo' | 'author' | 'label' | 'assignee', value: string) => {
    let newQuery: string
    switch (type) {
      case 'repo':
        newQuery = addRepoFilterToQuery(fetchedQuery, value)
        break
      case 'author':
        newQuery = addAuthorFilterToQuery(fetchedQuery, value)
        break
      case 'label':
        newQuery = addLabelFilterToQuery(fetchedQuery, value)
        break
      case 'assignee':
        newQuery = addAssigneeFilterToQuery(fetchedQuery, value)
        break
      default: {
        const _exhaustive: never = type
        return _exhaustive
      }
    }
    methods.updateAndSubmitQuery(newQuery)
  }

  const retryFailedRefresh = async (focusOrigin: HTMLElement | null) => {
    if (isRetrying) return

    const retryTarget = {
      query: fetchedQuery,
      page: requestedPage,
      focusOrigin,
    }
    const retryAttempt = ++retryAttemptRef.current
    recoveredRetryRef.current = undefined
    setRetryingTarget(retryTarget)
    announce('Retrying pull request refresh')
    try {
      const result = await retryFailedRefetch()
      if (!result.isRefetchError && retryAttempt === retryAttemptRef.current) recoveredRetryRef.current = retryTarget
    } finally {
      setRetryingTarget(currentTarget =>
        currentTarget?.query === retryTarget.query && currentTarget.page === retryTarget.page
          ? undefined
          : currentTarget,
      )
    }
  }

  return (
    <>
      {isCurrentFailedRefetch && (
        <Banner
          variant="warning"
          title="Unable to refresh pull requests."
          description="Showing previously loaded results."
          primaryAction={
            <Button
              loading={isRetrying}
              loadingAnnouncement=""
              onClick={event => {
                const focusOrigin =
                  event.detail === 0 && document.activeElement === event.currentTarget ? event.currentTarget : null
                void retryFailedRefresh(focusOrigin)
              }}
            >
              Retry
            </Button>
          }
        />
      )}
      <div className="tmp-my-3">
        <div {...hpcAttrs()} ref={resultsRegionRef} role="region" aria-label="Pull request results" tabIndex={-1}>
          {displayedData.error ? (
            <SharedDashboardBlankslate dashboardType="pull-requests" variant="error" />
          ) : (
            <>
              <PullsList
                pulls={displayedData.results}
                pageInfo={displayedData.pageInfo}
                compactMode={compactMode}
                onCompactModeChange={updateCompactModeSetting}
                onSortingItemSelect={sort => methods.updateAndSubmitQuery(addSortToQuery(fetchedQuery, sort))}
                sortingItemSelected={getSortFromQuery(fetchedQuery)}
                stateFilters={
                  stateQueryAnalysis.kind === 'ambiguous'
                    ? {kind: 'hidden'}
                    : {
                        kind: 'visible',
                        selected: stateQueryAnalysis.kind === 'selected' ? stateQueryAnalysis.state : undefined,
                        getLinkProps: getStateFilterLinkProps,
                      }
                }
                emptyListState={emptyListState}
                onFilterSelect={handleFilterSelect}
                isLoading={isNavigating || isLoading}
                isDeferredLoading={isPending}
                isAgentAttributionEnabled={isFeatureEnabled('agent_author_search_expansion_ui_pulls')}
              />
              {displayedData.pageInfo.totalPages > 1 && (
                <Pagination
                  pageCount={displayedData.pageInfo.totalPages}
                  currentPage={displayedData.pageInfo.currentPage}
                  onPageChange={(e, page) => {
                    e.preventDefault()

                    methods.fetchResults({q: fetchedQuery, page})
                    window.scrollTo({top: 0})
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}
